import { Button, IconButton, Typography } from '@mui/material';
import { useEffect, useState, ChangeEvent } from 'react';
import moment from 'moment';
import { ZodError, array } from 'zod';
import CopyToClipboard from 'react-copy-to-clipboard';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

import { ColumnSpec } from '../../components/common/DataTable/types';
import UserAvatar from '../../components/common/UserAvatar/UserAvatar';
import { User } from '../../components/Dashboard/types';
import getLocalStorageData, { setLocalStorageData } from '../../utils/token';
import getAPIUrl from '../../config';
import { getApi, parseError, postApiWithAuth, putApiWithAuth } from '../../utils/apiCalls';
import MyProfileDetails from '../../components/MyProfile/ProfileDetails';
import EditMyProfile from '../../components/MyProfile/EditProfile';
import { UserData } from '../../components/MyProfile/ProfileDetails/types';
import PartnersAndMembersManagement from '../../components/PatrnerAndMembersManagment';
import { IMembers, IPartner, IPatients, PatientAddress } from '../../components/PatrnerAndMembersManagment/types';
import styles from './styles';
import ViewPartner from '../../components/Partner/ViewPartner';
import EditPartner from '../../components/Partner/EditPartner';
import { StatusEnum, UserStatusEnum } from '../../utils/Enums';
import getMembers, { getPartners } from './api';
import Popup from '../../components/common/Popup/Popup';
import { SortDirection } from '../../components/common/DataTable/sortable-header';
import editProfileSchema, {
    updateMemberAddressSchema,
    addMemberAddressSchema,
    editMemberSchema,
} from '../../utils/schemas';
import { FEATURE_LEVEL, hasFeatureEnabled } from '../../utils/featureConstants';
import MemberDetails from '../../components/Member/MemberDetails';
import EditMember from '../../components/Member/EditMember';
import { EditMemberFormValues } from '../../components/Member/EditMember/types';
import { useToast } from '../../components/common/ToasterContext';
import { modifiedPlanTypeName, showLoaderAfterTimeout, transformPhoneForAPI, trimFormData } from '../../utils/helpers';
import AddMemberPrimaryAddress from '../../components/Member/AddMemberPrimaryAddress';
import { AddMemberPrimaryAddressFormValues } from '../../components/Member/AddMemberPrimaryAddress/types';
import {
    UpdateMemberAdditionalAddressFormErrors,
    UpdateMemberAdditionalAddressFormValues,
} from '../../components/Member/UpdateMemberAdditionalAddress/types';
import UpdateMemberAdditionalAddress from '../../components/Member/UpdateMemberAdditionalAddress';

export const getUserStatus = (userStatusId: string) => {
    switch (userStatusId) {
        case '1':
            return UserStatusEnum.INVITED;
        case '2':
            return UserStatusEnum.ACTIVE;
        case '3':
            return UserStatusEnum.ACCESS_REMOVED;
        case '4':
            return UserStatusEnum.ACCESS_REMOVED;
        default:
            return '';
    }
};

const PartnerAndMemberManagement = () => {
    const userData = getLocalStorageData();
    const [partnerData, setPartnerData] = useState<IPartner[]>([]);
    const [partnerCount, setPartnerCount] = useState<number>(0);
    const [selectedPeriod, setSelectedPeriod] = useState('');
    const [partnerHasMore, setPartnerHasMore] = useState(true);
    const [memberHasMore, setMemberHasMore] = useState(true);
    const [memberCurrentPage, setMemberCurrentPage] = useState<number>(1);
    const [partnerCurrentPage, setPartnerCurrentPage] = useState<number>(1);
    const [openProfileDetails, setOpenProfileDetails] = useState(false);
    const [editProfileDetails, setEditProfileDetails] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState<number>(0);
    const [selectedImage, setSelectedImage] = useState<string | ArrayBuffer | null | undefined>();
    const [selectedFile, setSelectedFile] = useState<File>();
    const [selectedMemberImage, setSelectedMemberImage] = useState<string | ArrayBuffer | null | undefined>();
    const [selectedMemberFile, setSelectedMemberFile] = useState<File>();
    const [patientConditionsObj, setPatientConditionsObj] = useState<{
        addConditions: Array<number>;
        deleteConditions: Array<number>;
    }>({ addConditions: [], deleteConditions: [] });
    const [showError, setShowError] = useState<boolean>(false);
    const [viewPartner, setViewPartner] = useState<boolean>(false);
    const [editPartner, setEditPartner] = useState<boolean>(false);
    const [rowPartnerData, setRowPartnerData] = useState<IPartner>();
    const [openMemberDetails, setOpenMemberDetails] = useState<boolean>(false);
    const [editMemberDetails, setEditMemberDetails] = useState<boolean>(false);
    const [rowMemberData, setRowMemberData] = useState<IMembers>();
    const [partnerAndMember, setPartnerAndMember] = useState<number>(0);
    const [membersCount, setMembersCount] = useState<number>(0);
    const [membersData, setMembersData] = useState<IMembers[]>([]);
    const [userInfo, setUserInfo] = useState<UserData>({
        ...userData,
        password: '',
        repeatPassword: '',
        roleId: '',
    });
    const [memberInfo, setMemberInfo] = useState<EditMemberFormValues>({
        email: '',
        employer: { createdAt: '', name: '', email: '', logoUrl: '' },
        employerId: '',
        firstname: '',
        gender: 1,
        lastname: '',
        patientCondition: [],
        phone: '',
        signedImageUrl: '',
        userStatusId: '',
        otherType: '',
        subscriberId: '',
        dateOfBirth: '',
        addressId: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
    });
    const [changeStatus, setChangeStatus] = useState(false);
    const [diabetesTypeData, setDiabetesTypeData] = useState<
        Array<{
            value: number;
            label: string;
            checked: boolean;
        }>
    >([
        {
            value: 1,
            label: 'Type 1',
            checked: false,
        },
        {
            value: 2,
            label: 'Type 2',
            checked: false,
        },
        {
            value: 3,
            label: 'LADA',
            checked: false,
        },
        {
            value: 4,
            label: 'Gestational',
            checked: false,
        },
        {
            value: 5,
            label: 'Other',
            checked: false,
        },
    ]);
    const [formErrors, setFormErrors] = useState<{
        [key: string]: Array<string>;
    }>();
    const [memberFormErrors, setMemberFormErrors] = useState<{
        [key: string]: Array<string>;
    }>();
    const { state } = useLocation();
    const isMember = (state as { members: boolean })?.members;
    const [roles, setRoles] = useState<Array<{ name: string; value: number }>>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedMemberStatus, setSelectedMemberStatus] = useState('');
    const [selectedMemberPeriod, setSelectedMemberPeriod] = useState('');
    const [partnerSearchText, setPartnerSearchText] = useState('');
    const [memberSearchText, setMemberSearchText] = useState('');
    const [applyPartnerFilters, setApplyPartnerFilters] = useState(false);
    const [applyMemberFilters, setApplyMemberFilters] = useState(false);
    const [loader, setLoader] = useState(true);
    const [createMember, setCreateMember] = useState(false);
    const [allPartnerData, setAllPartnerData] = useState<IPartner[]>([]);
    const [partnerSortKey, setPartnerSortkey] = useState<string>('name');
    const [partnerSortOrder, setPartnerSortOrder] = useState<string>('asc');
    const [memberSortKey, setMemberSortkey] = useState<string>('name');
    const [memberSortOrder, setMemberSortOrder] = useState<string>('asc');
    const [memberAddressess, setMemberAddressess] = useState<PatientAddress[] | undefined>([]);
    const [addMemberPrimaryAddress, setAddMemberPrimaryAddress] = useState<boolean>(false);
    const [memberAddressFormErrors, setMemberAddressFormErrors] = useState<{
        [key: string]: Array<string>;
    }>();
    const primaryAddressDefaultValues = {
        firstname: '',
        lastname: '',
        employerId: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
    };
    const [memberPrimaryAddressInfo, setMemberPrimaryAddressInfo] =
        useState<AddMemberPrimaryAddressFormValues>(primaryAddressDefaultValues);
    const [updateMemberAdditionalAddress, setUpdateMemberAdditionalAddress] = useState<boolean>(false);
    const [memberAdditionalAddressFormErrors, setMemberAdditionalAddressFormErrors] =
        useState<UpdateMemberAdditionalAddressFormErrors>();
    const updateAddressDefaultValues = [
        {
            id: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
        },
    ];
    const [memberAdditionalAddressInfo, setMemberAdditionalAddressInfo] =
        useState<UpdateMemberAdditionalAddressFormValues[]>(updateAddressDefaultValues);
    const [showViewMoreAddresses, setShowViewMoreAddresses] = useState<boolean>(false);
    const [numberOfAddressShowing, setNumberOfAddressShowing] = useState<number>(0);
    const additionalAddressesToLoad = 3;
    const [submitting, setSubmitting] = useState<boolean>(false);

    const toast = useToast();
    const size = 10;
    const [reload, setReload] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (isMember) {
            setPartnerAndMember(1);
        } else {
            setPartnerAndMember(0);
        }
    }, []);

    const user: User = {
        name: `${userData.firstname} ${userData.lastname}`,
        profileImg: userData.signedImageUrl,
    };

    const onStatusChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSelectedStatus(event.target.value);
    };

    const onPeriodChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSelectedPeriod(event.target.value);
    };

    const onMemberStatusChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSelectedMemberStatus(event.target.value);
    };

    const onMemberPeriodChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSelectedMemberPeriod(event.target.value);
    };

    const onDiabetesTypeChange = (name: string, checked: boolean) => {
        const data = diabetesTypeData.map((c) => (c.label === name ? { ...c, checked } : c));
        setDiabetesTypeData(data);
    };

    const onMemberFiltersClearClick = () => {
        setSelectedMemberStatus('');
        setSelectedMemberPeriod('');
        setDiabetesTypeData(diabetesTypeData.map((dt) => ({ ...dt, checked: false })));
        setApplyMemberFilters(!applyMemberFilters);
    };

    const onMemberFilterChipDelete = (type: 'status' | 'diabetes' | 'additionPeriod', name?: string) => {
        if (type === 'status') {
            setSelectedMemberStatus('');
        } else if (type === 'diabetes' && name) {
            const data = diabetesTypeData.map((dt) => (dt.label === name ? { ...dt, checked: false } : dt));
            setDiabetesTypeData(data);
        } else {
            setSelectedMemberPeriod('');
        }
        setApplyMemberFilters(!applyMemberFilters);
    };

    const onPartnerFiltersClearClick = () => {
        setSelectedStatus('');
        setSelectedPeriod('');
        setApplyPartnerFilters(!applyPartnerFilters);
    };

    const onPartnerFilterChipDelete = (type: 'status' | 'additionPeriod') => {
        if (type === 'status') {
            setSelectedStatus('');
        } else {
            setSelectedPeriod('');
        }
        setApplyPartnerFilters(!applyPartnerFilters);
    };

    const onPartnerFiltersApplyClick = () => {
        setPartnerData([]);
        setPartnerCurrentPage(1);
        setApplyPartnerFilters(!applyPartnerFilters);
    };

    const onMemberFiltersApplyClick = () => {
        setPartnerData([]);
        setMemberCurrentPage(1);
        setApplyMemberFilters(!applyMemberFilters);
    };

    const onPartnerSearchChange = (text: string) => {
        setPartnerSearchText(text);
        setPartnerCurrentPage(1);
    };

    const onMemberSearchChange = (text: string) => {
        setMemberSearchText(text);
        setMemberCurrentPage(1);
    };

    const partnerColumnSpec: ColumnSpec<IPartner>[] = [
        {
            name: 'name',
            title: 'Partner',
            cellProperty: 'name',
            cellRenderer: (partner: IPartner) => (
                <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} component="div">
                    <UserAvatar
                        name={`${partner.name}`}
                        size={40}
                        source=""
                        radius="20px"
                        bgColor={partner.status === StatusEnum.Active ? '#6773BA' : '#8697AB'}
                        id={`${partner.name}_icon`}
                    />
                    <Typography
                        component="span"
                        sx={styles.title}
                        id={`partner_${partner.name}`}
                        onClick={() => {
                            navigate(`/partner-member/selected-partner-member`, {
                                state: {
                                    id: partner.id,
                                    name: partner.name,
                                    email: partner.email,
                                    signedImageUrl: partner.logoUrl,
                                    createdAt: partner.createdAt,
                                    uniqueId: partner.uniqueId,
                                    status: partner.status,
                                },
                            });
                        }}
                    >
                        {partner.name}
                    </Typography>
                </Typography>
            ),
        },
        {
            name: 'createdAt',
            title: 'Added',
            cellProperty: 'createdAt',
            cellRenderer: (partner: IPartner) => <>{moment(new Date(partner.createdAt)).format('MMM DD, YYYY')}</>,
        },
        {
            name: 'patients',
            title: 'Members',
            cellProperty: 'patients',
            cellRenderer: (partner: IPartner) => (
                <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} component="div">
                    {partner.patients
                        .filter((_it, i) => i <= 1)
                        .map((p: IPatients, index: number) => (
                            <Typography key={`${p.id}_${partner.id}`} sx={{ marginLeft: index !== 0 ? '-7px' : '0px' }}>
                                <UserAvatar
                                    name={`${p.firstname} ${p.lastname}`}
                                    size={40}
                                    source=""
                                    radius="20px"
                                    bgColor={index ? '#DF7BD5' : '#DF7B7B'}
                                />
                            </Typography>
                        ))}
                    {partner.patients.length ? (
                        partner.patients.length - 2 > 0 && (
                            <Typography sx={styles.countText(40, '20px', 12, '#E0E4ED')}>
                                {`+${partner.patients.length - 2}`}
                            </Typography>
                        )
                    ) : (
                        <Typography sx={styles.countText(40, '20px', 12, '#E0E4ED')}>{0}</Typography>
                    )}
                </Typography>
            ),
        },
        {
            name: 'Plan',
            title: 'Plan Type',
            cellProperty: 'Plan',
            sortVisible: false,
            cellRenderer: (partner: IPartner) => <>{modifiedPlanTypeName(partner.Plan?.id)}</>,
        },
        {
            name: 'status',
            title: 'Status',
            cellProperty: 'status',
            cellRenderer: (partner: IPartner) => (
                <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} component="div">
                    <Typography
                        component="span"
                        sx={partner.status === StatusEnum.Active ? styles.active : styles.inActive}
                    >
                        {partner.status}
                    </Typography>
                </Typography>
            ),
        },
        {
            name: 'action',
            title: 'Action',
            cellProperty: 'action',
            sortVisible: false,
            cellRenderer: (partner) => (
                <IconButton
                    id="edit-action"
                    onClick={() => {
                        setRowPartnerData(partner);
                        setViewPartner(true);
                    }}
                >
                    <img alt="status" src="/images/edit.png" />
                </IconButton>
            ),
        },
    ];

    const memberColumnSpec: ColumnSpec<IMembers>[] = [
        {
            name: 'name',
            title: 'Member',
            cellProperty: 'firstname',
            cellRenderer: (member: IMembers) => (
                <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} component="div">
                    <UserAvatar
                        name={`${member.firstname} ${member.lastname}`}
                        size={32}
                        source={member.signedImageUrl || ''}
                        radius="20px"
                    />
                    <Typography
                        component="span"
                        sx={styles.title}
                        onClick={() => {
                            setRowMemberData(member);
                            const primaryAddress = member?.patientAddress?.find((a) => a.isDefault);
                            setMemberInfo({
                                signedImageUrl: member.signedImageUrl,
                                firstname: member.firstname,
                                lastname: member.lastname,
                                email: member.email,
                                patientCondition: member.patientCondition
                                    .filter((it) => ['1', '2', '3', '4', '5'].includes(it.condition.id))
                                    .map((it) => Number(it.condition.id)),
                                gender: member.gender,
                                employer: member.employer,
                                employerId: String(member.employerId),
                                phone: member.phone,
                                subscriberId: member.subscriberId,
                                dateOfBirth: member.dateOfBirth,
                                userStatusId: member?.userStatusId,
                                otherType:
                                    member.patientCondition.find(
                                        (it) => !['1', '2', '3', '4', '5'].includes(it.condition.id),
                                    )?.condition.subtype || '',
                                addressId: primaryAddress?.id,
                                address1: primaryAddress?.address1,
                                address2: primaryAddress?.address2,
                                city: primaryAddress?.city,
                                state: primaryAddress?.state,
                                zip: primaryAddress?.zip,
                            });
                            setOpenMemberDetails(true);
                            setMemberAddressess(member?.patientAddress);
                        }}
                    >
                        {member.firstname} {member.lastname}
                    </Typography>
                </Typography>
            ),
        },
        {
            name: 'email',
            title: 'Email',
            cellProperty: 'email',
            cellRenderer: (member: IMembers) => (
                <CopyToClipboard text={member.email}>
                    <Typography sx={styles.email}>
                        <img src="/images/copy-to-clipboard.png" alt="Copy To clip board" />
                        <Typography component="span" sx={styles.rowLabel}>
                            {member.email}
                        </Typography>
                    </Typography>
                </CopyToClipboard>
            ),
        },
        // {
        //     name: 'dateOfBirth',
        //     title: 'Date of Birth',
        //     cellProperty: 'dateOfBirth',
        //     cellRenderer: (member: IMembers) => (
        //         <CopyToClipboard text={member.email}>
        //             <Typography sx={styles.email}>
        //                 <Typography component="span" sx={styles.rowLabel}>
        //                     {member?.dateOfBirth ? moment(member.dateOfBirth).format('MM/DD/YYYY') : ''}
        //                 </Typography>
        //             </Typography>
        //         </CopyToClipboard>
        //     ),
        // },
        // {
        //     name: 'subscriberId',
        //     title: 'Subscriber ID',
        //     cellProperty: 'subscriberId',
        //     sortVisible: false,
        //     cellRenderer: (member: IMembers) => (
        //         <CopyToClipboard text={member.email}>
        //             <Typography sx={styles.email}>
        //                 <Typography component="span" sx={styles.rowLabel}>
        //                     {member?.subscriberId}
        //                 </Typography>
        //             </Typography>
        //         </CopyToClipboard>
        //     ),
        // },
        {
            name: 'employer',
            title: 'Partner',
            cellProperty: 'employer',
            cellRenderer: (member: IMembers) => (
                <Typography sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} component="div">
                    <UserAvatar
                        name={`${member.employer.name}`}
                        size={32}
                        source={member.employer.logoUrl || ''}
                        radius="20px"
                    />
                    <Typography component="span" sx={styles.userTitle}>
                        {member.employer.name}
                    </Typography>
                </Typography>
            ),
        },
        {
            name: 'createdAt',
            title: 'Account Created',
            cellProperty: 'createdAt',
            cellRenderer: (member: IMembers) => (
                <Typography component="span" sx={styles.dateText}>
                    {moment(member?.createdAt).format('MM/DD/YYYY')}
                </Typography>
            ),
        },
        {
            name: 'patientLoginAudit',
            title: 'Last Login',
            cellProperty: 'patientLoginAudit',
            sortVisible: false,
            cellRenderer: (member: IMembers) => (
                <Typography component="span" sx={styles.dateText}>
                    {member?.patientLoginAudit?.length
                        ? moment(member?.patientLoginAudit?.[0].createdAt).format('MM/DD/YYYY')
                        : ''}
                </Typography>
            ),
        },
        {
            name: 'patientCondition',
            title: 'Diabetes Type',
            cellProperty: 'patientCondition',
            sortVisible: false,
            cellRenderer: (member: IMembers) => (
                <Typography component="span" sx={styles.rowLabel}>
                    {member.patientCondition.map((it) => it.condition.subtype).join(',')}
                </Typography>
            ),
        },
        {
            name: 'userStatusId',
            title: 'Status',
            cellProperty: 'userStatusId',
            cellRenderer: (member: IMembers) => (
                <Typography
                    component="div"
                    sx={styles.rowLabel}
                    onClick={() => {
                        setChangeStatus(true);
                        setRowMemberData(member);
                    }}
                    id="all_members_status"
                >
                    {getUserStatus(member.userStatusId)}
                    <br />
                    <img
                        alt="status"
                        src={
                            member.userStatusId === '1' || member.userStatusId === '2'
                                ? '/images/active-toggle.png'
                                : '/images/disable-toggle.png'
                        }
                    />
                </Typography>
            ),
        },
    ];

    useEffect(() => {
        getApi(`${getAPIUrl()}/admins/admin-roles`).then((res) => {
            const rolesData = res.data.data.data.map((r: { role: string; id: string }) => ({
                name: r.role,
                value: Number(r.id),
            }));
            setRoles(rolesData);
            const roleData = rolesData.find((role: { name: string; value: number }) => role.name === userData.roleName);
            setSelectedRoleId(roleData?.value);
            setUserInfo({ ...userInfo, roleId: String(roleData?.value) });
        });
    }, []);

    useEffect(() => {
        setLoader(true);
        getPartners({
            currentPage: 1,
            partnerSearchText: '',
            partnerSize: 100000000,
            selectedPartnerPeriod: '',
            selectedPartnerStatus: '',
            sort: 'name',
            order: 'asc',
        })
            .then((res) => {
                setAllPartnerData(res.data.data.rows);
                showLoaderAfterTimeout(setLoader);
            })
            .catch((_error) => {
                showLoaderAfterTimeout(setLoader);
            });
    }, []);

    useEffect(() => {
        setLoader(true);
        if (partnerAndMember === 1 || !hasFeatureEnabled(FEATURE_LEVEL.viewPartnerEmployer)) {
            getMembers({
                currentPage: memberCurrentPage,
                memberSearchText,
                memberSize: size,
                selectedDiabetesType: diabetesTypeData,
                selectedMemberPeriod,
                selectedMemberStatus,
                sort: memberSortKey,
                order: memberSortOrder,
            })
                .then((res) => {
                    setMembersData(res.data.data.rows);
                    setMembersCount(res.data.data.count);
                    showLoaderAfterTimeout(setLoader);
                })
                .catch((_error) => {
                    showLoaderAfterTimeout(setLoader);
                });
        } else {
            getPartners({
                currentPage: partnerCurrentPage,
                partnerSearchText,
                partnerSize: size,
                selectedPartnerPeriod: selectedPeriod,
                selectedPartnerStatus: selectedStatus,
                sort: partnerSortKey,
                order: partnerSortOrder,
            })
                .then((res) => {
                    setPartnerData(res.data.data.rows);
                    setPartnerCount(res.data.data.count);
                    showLoaderAfterTimeout(setLoader);
                })
                .catch((_error) => {
                    showLoaderAfterTimeout(setLoader);
                });

            if (membersCount === 0) {
                getMembers({
                    currentPage: memberCurrentPage,
                    memberSearchText,
                    memberSize: size,
                    selectedDiabetesType: diabetesTypeData,
                    selectedMemberPeriod,
                    selectedMemberStatus,
                    sort: memberSortKey,
                    order: memberSortOrder,
                })
                    .then((res) => {
                        setMembersData(res.data.data.rows);
                        setMembersCount(res.data.data.count);
                        showLoaderAfterTimeout(setLoader);
                    })
                    .catch((_error) => {
                        showLoaderAfterTimeout(setLoader);
                    });
            }
        }
    }, [partnerAndMember, applyMemberFilters, applyPartnerFilters, reload, memberSearchText, partnerSearchText]);

    const memberLoadMore = () => {
        const loadItems = async () => {
            if (size * (memberCurrentPage - 1) < membersCount) {
                const res = await getMembers({
                    currentPage: memberCurrentPage + 1,
                    memberSearchText,
                    memberSize: size,
                    selectedDiabetesType: diabetesTypeData,
                    selectedMemberPeriod,
                    selectedMemberStatus,
                    sort: memberSortKey,
                    order: memberSortOrder,
                });
                if (!axios.isAxiosError(res)) {
                    const mData = [...membersData, ...res.data.data.rows];
                    if (!(mData.length === membersCount)) {
                        setMemberCurrentPage(memberCurrentPage + 1);
                    }
                    setMembersData(mData);
                    showLoaderAfterTimeout(setLoader);
                }
            }
        };
        setMemberHasMore(!(membersData.length === membersCount));
        loadItems();
    };

    const partnerLoadMore = () => {
        const loadItems = async () => {
            if (size * (partnerCurrentPage - 1) < partnerCount) {
                const res = await getPartners({
                    currentPage: partnerCurrentPage + 1,
                    partnerSearchText,
                    partnerSize: size,
                    selectedPartnerPeriod: selectedPeriod,
                    selectedPartnerStatus: selectedStatus,
                    sort: partnerSortKey,
                    order: partnerSortOrder,
                });
                if (!axios.isAxiosError(res)) {
                    const pData = [...partnerData, ...res.data.data.rows];
                    if (!(partnerData.length === partnerCount)) {
                        setPartnerCurrentPage(partnerCurrentPage + 1);
                    }
                    setPartnerData(pData);
                    showLoaderAfterTimeout(setLoader);
                }
            }
        };
        setPartnerHasMore(!(partnerData.length === partnerCount));
        loadItems();
    };

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            editProfileSchema.parse(trimFormData(userInfo));
            const formData = new FormData();
            formData.append('roleId', String(selectedRoleId));
            formData.append('firstname', userInfo.firstname);
            formData.append('lastname', userInfo.lastname);
            formData.append('imageFile', selectedFile as any);
            if (userInfo.password) {
                formData.append('password', String(userInfo.password || ''));
            }
            if (userInfo.repeatPassword) {
                formData.append('repeatPassword', String(userInfo.repeatPassword || ''));
            }

            const result = await putApiWithAuth(`${getAPIUrl()}/admins/edit-admin`, formData);
            setUserInfo({
                ...userInfo,
                email: result.data.data.data.email,
                firstname: result.data.data.data.firstname,
                lastname: result.data.data.data.lastname,
                profileImageUrl: result.data.data.data.profileImageUrl,
                signedImageUrl: result.data.data.data.signedImageUrl,
                roleName: roles.find((r) => r.value === Number(result.data.data.data.roleId))?.name || '',
                password: '',
                repeatPassword: '',
            });
            setLocalStorageData({
                ...userData,
                adminRole: {
                    role: roles.find((r) => r.value === Number(result.data.data.data.roleId))?.name || '',
                },
                email: result.data.data.data.email,
                firstname: result.data.data.data.firstname,
                lastname: result.data.data.data.lastname,
                profileImageUrl: result.data.data.data.profileImageUrl,
                signedImageUrl: result.data.data.data.signedImageUrl,
            });
            setSelectedRoleId(result.data.data.data.roleId);
            setEditProfileDetails(false);
            setOpenProfileDetails(false);
            if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage('Request Processeed');
                toast.onSuccess();
            }
        } catch (error: any) {
            if (error instanceof ZodError) {
                setFormErrors(error.flatten().fieldErrors);
            } else if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage(error.response.data.errors);
                toast.onError();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleMemberSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            editMemberSchema.parse(trimFormData(memberInfo));
            const formData = new FormData();
            const otherType = { name: 'Diabetes', subtype: memberInfo.otherType };
            formData.append('gender', String(memberInfo.gender));
            formData.append('firstname', memberInfo.firstname);
            formData.append('lastname', memberInfo.lastname);
            formData.append('employerId', String(memberInfo.employerId));
            formData.append('phone', memberInfo.phone);
            if (memberInfo?.dateOfBirth) {
                formData.append('dateOfBirth', memberInfo.dateOfBirth);
            }
            if (memberInfo?.subscriberId !== null && memberInfo?.subscriberId !== undefined) {
                formData.append('subscriberId', memberInfo.subscriberId);
            }
            patientConditionsObj.addConditions
                .filter((it) => it !== 5)
                .forEach((pa) => formData.append('addConditions[]', pa as any));
            patientConditionsObj.deleteConditions
                .filter((it) => it !== 5)
                .forEach((pd) => formData.append('deleteConditions[]', pd as any));
            formData.append('imageFile', selectedMemberFile as any);
            if (otherType.subtype) {
                formData.append('otherType', JSON.stringify(otherType));
            }
            if (memberInfo.addressId) {
                formData.append('addressId', memberInfo.addressId);
                formData.append('address1', String(memberInfo?.address1));
                if (memberInfo?.address2 !== null && memberInfo?.address2 !== undefined) {
                    formData.append('address2', String(memberInfo?.address2));
                }
                formData.append('city', String(memberInfo.city));
                formData.append('state', String(memberInfo.state));
                formData.append('zip', String(memberInfo.zip));
            }
            await putApiWithAuth(`${getAPIUrl()}/employers/update-member/${rowMemberData?.id}`, formData);
            setMemberFormErrors(undefined);
            setEditMemberDetails(false);
            setOpenMemberDetails(false);
            if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage('Request Processeed');
                toast.onSuccess();
            }
            setReload(!reload);
        } catch (error: any) {
            if (error instanceof ZodError) {
                setMemberFormErrors(error.flatten().fieldErrors);
            } else if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage(error.response.data.errors);
                toast.onError();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value, name } = e.target;
        if (name === 'roleId') {
            setSelectedRoleId(Number(value));
        }
        setUserInfo({
            ...userInfo,
            [name]: String(value),
        });
    };

    const handleMemberInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value, name } = e.target;
        switch (name) {
            case 'gender':
                setMemberInfo({
                    ...memberInfo,
                    [name]: value as unknown as number,
                });
                break;
            case 'patientCondition':
                setMemberInfo({
                    ...memberInfo,
                    [name]: value as unknown as Array<number>,
                });
                break;
            case 'phone':
                setMemberInfo({
                    ...memberInfo,
                    [name]: transformPhoneForAPI(value),
                });
                break;
            case 'state':
                setMemberInfo({
                    ...memberInfo,
                    [name]: value as unknown as string,
                });
                break;
            default:
                setMemberInfo({
                    ...memberInfo,
                    [name]: String(value),
                });
                break;
        }
    };

    const handleCheckItem = (checked: boolean, item: number) => {
        if (checked) {
            setPatientConditionsObj({
                addConditions: [...patientConditionsObj.addConditions, item],
                deleteConditions: patientConditionsObj.deleteConditions.filter((pd) => pd !== item),
            });
        } else {
            setPatientConditionsObj({
                deleteConditions: [...patientConditionsObj.deleteConditions, item],
                addConditions: patientConditionsObj.addConditions.filter((pd) => pd !== item),
            });
        }
    };

    const handleChangeAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
            const reader = new FileReader();
            reader.onload = (event) => {
                setSelectedImage(event?.target?.result);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const handleMemberChangeAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedMemberFile(e.target.files[0]);
            const reader = new FileReader();
            reader.onload = (event) => {
                setSelectedMemberImage(event?.target?.result);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const partnerSortData = async (sortKey: string, direction: SortDirection) => {
        const res = await getPartners({
            currentPage: 1,
            partnerSearchText,
            partnerSize: size,
            selectedPartnerPeriod: selectedPeriod,
            selectedPartnerStatus: selectedStatus,
            sort: sortKey,
            order: direction === 0 ? 'desc' : 'asc',
        });
        if (!axios.isAxiosError(res)) {
            setPartnerData([...res.data.data.rows]);
            setPartnerCount(res.data.data.count);
            setPartnerSortkey(sortKey);
            setPartnerSortOrder(direction === 0 ? 'desc' : 'asc');
            setPartnerHasMore(true);
            setPartnerCurrentPage(1);

            showLoaderAfterTimeout(setLoader);
        }
    };

    const memberSortData = async (sortKey: string, direction: SortDirection) => {
        const res = await getMembers({
            currentPage: 1,
            memberSearchText,
            memberSize: size,
            selectedDiabetesType: diabetesTypeData,
            selectedMemberPeriod,
            selectedMemberStatus,
            sort: sortKey,
            order: direction === 0 ? 'desc' : 'asc',
        });
        if (!axios.isAxiosError(res)) {
            setMembersData([...res.data.data.rows]);
            setMembersCount(res.data.data.count);
            setMemberSortkey(sortKey);
            setMemberSortOrder(direction === 0 ? 'desc' : 'asc');
            setMemberHasMore(true);
            setMemberCurrentPage(1);

            showLoaderAfterTimeout(setLoader);
        }
    };

    const handleCloseMyProfile = (value: boolean) => {
        setUserInfo({
            ...userData,
            password: '',
            repeatPassword: '',
            roleId: String(
                roles.find((role: { name: string; value: number }) => role.name === userData.roleName)?.value,
            ),
        });
        setEditProfileDetails(value);
        setFormErrors(undefined);
    };

    const handleCloseAddMemberPrimaryAddress = () => {
        setMemberAddressFormErrors(undefined);
        setAddMemberPrimaryAddress(false);
        setMemberPrimaryAddressInfo(primaryAddressDefaultValues);
    };

    const handleMemberPriamryAddressSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            addMemberAddressSchema.parse(trimFormData(memberPrimaryAddressInfo));
            const payload = { ...memberPrimaryAddressInfo };
            //  deleting these two which are not useful in adding address
            delete payload?.firstname;
            delete payload?.lastname;
            await postApiWithAuth(`${getAPIUrl()}/employers/create-member-address/${rowMemberData?.id}`, payload);
            handleCloseAddMemberPrimaryAddress();
            setEditMemberDetails(false);
            setOpenMemberDetails(false);
            if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage('Request Processeed');
                toast.onSuccess();
            }
            setReload(!reload);
        } catch (error: any) {
            if (error instanceof ZodError) {
                setMemberAddressFormErrors(error.flatten().fieldErrors);
            } else if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage(error.response.data.errors);
                toast.onError();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleMemberPrimaryAddressInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value, name } = e.target;
        switch (name) {
            case 'state':
                setMemberPrimaryAddressInfo({
                    ...memberPrimaryAddressInfo,
                    [name]: value as unknown as string,
                });
                break;
            default:
                setMemberPrimaryAddressInfo({
                    ...memberPrimaryAddressInfo,
                    [name]: String(value),
                });
                break;
        }
    };

    const handleCloseUpdateMemberAdditionalAddress = () => {
        setMemberAdditionalAddressFormErrors(undefined);
        setUpdateMemberAdditionalAddress(false);
        setMemberAdditionalAddressInfo(updateAddressDefaultValues);
        setNumberOfAddressShowing(0);
        setShowViewMoreAddresses(false);
    };

    const handleMemberAdditionalAddressesSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            array(updateMemberAddressSchema).nonempty().parse(trimFormData(memberAdditionalAddressInfo, 'array'));
            await putApiWithAuth(
                `${getAPIUrl()}/employers/update-member-addresses/${rowMemberData?.id}`,
                memberAdditionalAddressInfo,
                {
                    employerId: String(rowMemberData?.employerId),
                },
            );
            handleCloseUpdateMemberAdditionalAddress();
            setEditMemberDetails(false);
            setOpenMemberDetails(false);
            if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage('Request Processeed');
                toast.onSuccess();
            }
            setReload(!reload);
        } catch (error: any) {
            if (error instanceof ZodError) {
                const modifiedErrorsObj: any = {};
                error.errors.forEach((er) => {
                    if (!modifiedErrorsObj[er.path[0]]) {
                        modifiedErrorsObj[er.path[0]] = { [er.path[1]]: er.message };
                    } else if (!modifiedErrorsObj[er.path[0]][er.path[1]]) {
                        modifiedErrorsObj[er.path[0]] = { ...modifiedErrorsObj[er.path[0]], [er.path[1]]: er.message };
                    }
                });
                setMemberAdditionalAddressFormErrors(modifiedErrorsObj);
            } else if (toast) {
                const err = parseError(error);
                if (err) {
                    toast.onToggleSnackbar();
                    toast.onMessage(err);
                    toast.onError();
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleMemberAdditionalAddressInput = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        i: number,
    ): void => {
        const { value, name } = e.target;
        const additionalAddressFormValues = memberAdditionalAddressInfo as any;
        switch (name) {
            case 'state':
                additionalAddressFormValues[i][name] = value as unknown as string;
                break;
            default:
                additionalAddressFormValues[i][name] = String(value);
                break;
        }

        setMemberAdditionalAddressInfo([...additionalAddressFormValues]);
    };

    const handleUpdateAdditionalAddress = () => {
        const additionalAddress = rowMemberData?.patientAddress
            ?.filter((a) => !a.isDefault)
            .map((a) => {
                return {
                    id: a.id,
                    address1: a.address1,
                    address2: a.address2,
                    city: a.city,
                    state: a.state,
                    zip: a.zip,
                };
            }) as unknown as UpdateMemberAdditionalAddressFormValues[];
        if (additionalAddress.length > additionalAddressesToLoad) {
            setMemberAdditionalAddressInfo(additionalAddress.slice(0, additionalAddressesToLoad));
            setNumberOfAddressShowing(additionalAddressesToLoad);
            setShowViewMoreAddresses(true);
        } else {
            setMemberAdditionalAddressInfo(additionalAddress);
        }

        setUpdateMemberAdditionalAddress(true);
    };

    const handleViewMoreAddress = () => {
        const additionalAddress = rowMemberData?.patientAddress
            ?.filter((a) => !a.isDefault)
            .map((a) => {
                return {
                    id: a.id,
                    address1: a.address1,
                    address2: a.address2,
                    city: a.city,
                    state: a.state,
                    zip: a.zip,
                };
            }) as unknown as UpdateMemberAdditionalAddressFormValues[];
        const numberOfAddressToLoad =
            additionalAddress.length > numberOfAddressShowing + additionalAddressesToLoad
                ? numberOfAddressShowing + additionalAddressesToLoad
                : additionalAddress.length;
        const additionalAddressFormValues = memberAdditionalAddressInfo as any;
        setMemberAdditionalAddressInfo([
            ...additionalAddressFormValues,
            ...additionalAddress.slice(numberOfAddressShowing, numberOfAddressToLoad),
        ]);
        setNumberOfAddressShowing(numberOfAddressToLoad);
        setShowViewMoreAddresses(additionalAddress.length > numberOfAddressToLoad);
    };

    return (
        <>
            <PartnersAndMembersManagement
                partnerColumns={partnerColumnSpec}
                memberColumns={memberColumnSpec}
                partnerData={partnerData}
                memberData={membersData}
                user={user}
                loader={loader}
                partnerLoadMore={partnerLoadMore}
                memberLoadMore={memberLoadMore}
                partnerHasMore={partnerHasMore}
                memberHasMore={memberHasMore}
                onPartnerSearchChange={onPartnerSearchChange}
                onMemberSearchChange={onMemberSearchChange}
                selectedStatus={selectedStatus}
                onStatusChange={onStatusChange}
                onMemberFiltersClearClick={onMemberFiltersClearClick}
                onMemberFilterChipDelete={onMemberFilterChipDelete}
                onPartnerFiltersClearClick={onPartnerFiltersClearClick}
                onPartnerFilterChipDelete={onPartnerFilterChipDelete}
                onPartnerFiltersApplyClick={onPartnerFiltersApplyClick}
                onMemberFiltersApplyClick={onMemberFiltersApplyClick}
                openProfileDetails={openProfileDetails}
                setOpenProfileDetails={setOpenProfileDetails}
                onPeriodChange={onPeriodChange}
                selectedPeriod={selectedPeriod}
                partnerAndMember={partnerAndMember}
                setPartnerAndMember={setPartnerAndMember}
                membersCount={membersCount}
                partnersCount={partnerCount}
                selectedMemberStatus={selectedMemberStatus}
                onMemberStatusChange={onMemberStatusChange}
                onMemberPeriodChange={onMemberPeriodChange}
                selectedMemberPeriod={selectedMemberPeriod}
                onDiabetesTypeChange={onDiabetesTypeChange}
                setCreateMember={setCreateMember}
                createMember={createMember}
                setReload={setReload}
                fromSelectedPartner={false}
                diabetesTypeData={diabetesTypeData}
                partnerSortData={partnerSortData}
                memberSortData={memberSortData}
                setPartnerCurrentPage={setPartnerCurrentPage}
                reload={reload}
            />
            <MyProfileDetails
                openProfileDetails={openProfileDetails}
                setOpenProfileDetails={setOpenProfileDetails}
                setEditProfileDetails={setEditProfileDetails}
                userData={userData}
            />
            <EditMyProfile
                editProfileDetails={editProfileDetails}
                userInfo={userInfo}
                handleInput={handleInput}
                handleSubmit={onSubmit}
                roles={roles}
                selectedRoleId={String(selectedRoleId)}
                handleChangeAvatar={handleChangeAvatar}
                selectedImage={selectedImage}
                formErrors={formErrors}
                handleCloseMyProfile={handleCloseMyProfile}
                submitting={submitting}
            />
            <ViewPartner
                setEditPartner={setEditPartner}
                setViewPartner={setViewPartner}
                viewPartner={viewPartner}
                rowPartnerData={rowPartnerData}
            />
            <MemberDetails
                memberData={rowMemberData}
                openMemberDetails={openMemberDetails}
                setOpenMemberDetails={setOpenMemberDetails}
                setEditMemberDetails={setEditMemberDetails}
                setChangeStatus={setChangeStatus}
                handleAddMemberPrimaryAddress={() => {
                    setMemberPrimaryAddressInfo({
                        ...memberPrimaryAddressInfo,
                        firstname: String(rowMemberData?.firstname),
                        lastname: String(rowMemberData?.lastname),
                        employerId: String(rowMemberData?.employerId),
                    });
                    setAddMemberPrimaryAddress(true);
                }}
                handleUpdateAdditionalAddress={handleUpdateAdditionalAddress}
            />
            <EditPartner
                setEditPartner={setEditPartner}
                setViewPartner={setViewPartner}
                editPartner={editPartner}
                rowPartnerData={rowPartnerData}
            />
            <EditMember
                editMemberDetails={editMemberDetails}
                memberInfo={memberInfo}
                handleInput={handleMemberInput}
                handleSubmit={handleMemberSubmit}
                handleChangeAvatar={handleMemberChangeAvatar}
                selectedImage={selectedMemberImage}
                formErrors={memberFormErrors}
                handleCloseEditMember={() => {
                    setMemberFormErrors(undefined);
                    setEditMemberDetails(false);
                }}
                handleCheckItem={handleCheckItem}
                partnersData={allPartnerData}
                fromSelectedPartner={false}
                setChangeStatus={setChangeStatus}
                setMemberInfo={setMemberInfo}
                memberAddressess={memberAddressess}
                handleAddMemberPrimaryAddress={() => {
                    setMemberPrimaryAddressInfo({
                        ...memberPrimaryAddressInfo,
                        firstname: String(rowMemberData?.firstname),
                        lastname: String(rowMemberData?.lastname),
                        employerId: String(rowMemberData?.employerId),
                    });
                    setAddMemberPrimaryAddress(true);
                }}
                handleUpdateAdditionalAddress={handleUpdateAdditionalAddress}
                submitting={submitting}
            />
            <AddMemberPrimaryAddress
                addMemberPrimaryAddress={addMemberPrimaryAddress}
                memberPrimaryAddressInfo={memberPrimaryAddressInfo}
                handleInput={handleMemberPrimaryAddressInput}
                handleSubmit={handleMemberPriamryAddressSubmit}
                formErrors={memberAddressFormErrors}
                handleCloseAddMemberPrimaryAddress={handleCloseAddMemberPrimaryAddress}
                submitting={submitting}
            />
            <UpdateMemberAdditionalAddress
                updateMemberAdditionalAddress={updateMemberAdditionalAddress}
                handleInput={handleMemberAdditionalAddressInput}
                handleSubmit={handleMemberAdditionalAddressesSubmit}
                formErrors={memberAdditionalAddressFormErrors}
                handleCloseUpdateMemberAdditionalAddress={handleCloseUpdateMemberAdditionalAddress}
                memberAdditionalAddressInfo={memberAdditionalAddressInfo}
                memberFirstName={String(rowMemberData?.firstname)}
                memberLastName={String(rowMemberData?.lastname)}
                showViewMoreAddresses={showViewMoreAddresses}
                handleViewMoreAddress={handleViewMoreAddress}
                submitting={submitting}
            />
            <Popup
                id={
                    rowMemberData?.userStatusId === '1' || rowMemberData?.userStatusId === '2'
                        ? 'confirmDeactivation'
                        : 'confirmActivation'
                }
                open={changeStatus}
                title={
                    rowMemberData?.userStatusId === '1' || rowMemberData?.userStatusId === '2'
                        ? 'Deactivate User'
                        : 'Activate User'
                }
                textAlign="center"
                handleClose={() => setChangeStatus(false)}
                description={
                    <>
                        {rowMemberData?.userStatusId === '1' || rowMemberData?.userStatusId === '2'
                            ? `Are you sure you want to make ${rowMemberData?.firstname}  ${rowMemberData?.lastname} inactive? Doing so will remove their access to the Diathrive Member Application.`
                            : `Are you sure you want to make ${rowMemberData?.firstname}  ${rowMemberData?.lastname} active? Doing so will give them access to the Diathrive Member Application.`}
                    </>
                }
            >
                <>
                    <Button
                        id="cancel"
                        variant="outlined"
                        sx={styles.cancel}
                        fullWidth
                        onClick={() => {
                            setChangeStatus(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        id={`continue${
                            rowMemberData?.userStatusId === '1' || rowMemberData?.userStatusId === '2' ? 'Dea' : 'A'
                        }ctivation`}
                        variant="contained"
                        sx={styles.confirm}
                        fullWidth
                        onClick={async () => {
                            try {
                                await putApiWithAuth(`${getAPIUrl()}/admins/update-member-status`, {
                                    memberId: Number(rowMemberData?.id),
                                    action:
                                        rowMemberData?.userStatusId === '1' || rowMemberData?.userStatusId === '2'
                                            ? 'Deactivate'
                                            : 'Activate',
                                });
                                setMemberCurrentPage(1);
                                const res = await getMembers({
                                    currentPage: 1,
                                    memberSearchText,
                                    memberSize: size,
                                    selectedDiabetesType: diabetesTypeData,
                                    selectedMemberPeriod,
                                    selectedMemberStatus,
                                    sort: 'firstname',
                                    order: 'asc',
                                });
                                setMembersData(res.data.data.rows);
                                setMembersCount(res.data.data.count);
                                const memberData = res.data.data.rows.find(
                                    (member: IMembers) => member.id === rowMemberData?.id,
                                );
                                setRowMemberData(memberData);
                                setMemberInfo({ ...memberInfo, userStatusId: memberData?.userStatusId });
                                setLoader(false);
                                setChangeStatus(false);
                            } catch (error) {
                                setShowError(!showError);
                            }
                        }}
                    >
                        Confirm
                    </Button>
                </>
            </Popup>
        </>
    );
};
export default PartnerAndMemberManagement;
